import { useState, useEffect, useCallback } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import {
  listStages,
  listCirclesByOrg,
  listStageCategorys,
} from '../../../../../../../graphql/queries';
import {
  Circle,
  Stage,
  LocationConfig,
  IntegrationConfig,
} from '../types/AccuLynxTypes';
import CrmService from '../../../../../../services/CrmService';
import fetchCircleLevels from '../../../../../../utils/fetchFunctions/fetchCircleLevels';
// Local query for IntegrationConfig until codegen is updated
const listIntegrationConfigsQuery = /* GraphQL */ `
  query ListIntegrationConfigs(
    $filter: ModelIntegrationConfigFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listIntegrationConfigs(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        orgID
        categoryID
        crmName
        stageMapping
        fieldMapping
        isActive
        apiKey
        configOptions
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Function to sort stages by sequence
const sortStages = (stages: Stage[]) => {
  return [...stages].sort((a, b) => {
    if (a.sequence === undefined || b.sequence === undefined) return 0;
    return a.sequence - b.sequence;
  });
};

export const useAccuLynxData = () => {
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCircles, setIsLoadingCircles] = useState(false);
  const [isLoadingStages, setIsLoadingStages] = useState(false);

  // Data states
  const [circles, setCircles] = useState<Circle[]>([]);
  const [paivStages, setPaivStages] = useState<Stage[]>([]);
  const [stageCategories, setStageCategories] = useState<any[]>([]);
  const [circleLevels, setCircleLevels] = useState<
    { id: string; name: string }[]
  >([]);
  const [config, setConfig] = useState<IntegrationConfig>({
    locations: [],
    stageMappings: [],
  });
  const [singleCrmCategoryId, setSingleCrmCategoryId] = useState<string | null>(
    null
  );

  // UI states
  const [isEditing, setIsEditing] = useState(false);

  // Saved state
  const [savedConfig, setSavedConfig] = useState<IntegrationConfig>({
    locations: [],
    stageMappings: [],
  });

  // Load existing data (integrations, circles, stages)
  const loadExistingData = useCallback(async () => {
    try {
      const orgID = (window as any).me?.orgID;
      if (!orgID) {
        console.error('No organization ID found');
        setIsLoading(false);
        return;
      }

      // Load circles
      const circlesResult = (await API.graphql(
        graphqlOperation(listCirclesByOrg, {
          orgID,
          filter: {
            isDeleted: { eq: false },
            isActive: { eq: true },
          },
          limit: 1000,
        })
      )) as GraphQLResult<{ listCirclesByOrg: { items: Circle[] } }>;

      const availableCircles = circlesResult.data?.listCirclesByOrg.items || [];
      setCircles(availableCircles);

      // Load existing integrations from IntegrationConfig
      const integrationsResult = (await API.graphql(
        graphqlOperation(listIntegrationConfigsQuery, {
          filter: {
            orgID: { eq: orgID },
            crmName: { eq: 'AccuLynx' },
            isActive: { eq: true },
          },
          limit: 1000,
        })
      )) as any;

      const rawItems =
        integrationsResult?.data?.listIntegrationConfigs?.items || [];
      const existingIntegrations = rawItems.filter((it: any) => !!it);

      if (existingIntegrations && existingIntegrations.length > 0) {
        const loadedLocations: LocationConfig[] = existingIntegrations.map(
          (item: any) => {
            const stageMappingObj = (() => {
              try {
                return JSON.parse(item.stageMapping || '{}');
              } catch {
                return {};
              }
            })();

            const configOptionsObj = (() => {
              try {
                let parsed = JSON.parse(item.configOptions || '{}');
                if (typeof parsed === 'string') parsed = JSON.parse(parsed);
                return parsed;
              } catch (error) {
                return {};
              }
            })();

            const parseStageMappings = (value: any): any[] => {
              if (!value) return [];
              try {
                const parsed =
                  typeof value === 'string' ? JSON.parse(value) : value;
                if (Array.isArray(parsed)) {
                  return parsed.map((m: any) => ({
                    accuLynxStage: String(m?.accuLynxStage ?? ''),
                    paivStage: String(m?.paivStage ?? ''),
                    paivStageId: String(m?.paivStageId ?? ''),
                  }));
                }
                if (parsed && typeof parsed === 'object') {
                  return Object.entries(parsed).map(
                    ([accuLynxStage, mapping]) => {
                      // mapping may be an object or a direct string id
                      const isString = typeof mapping === 'string';
                      const obj = isString
                        ? { paivStageId: mapping }
                        : (mapping as any);
                      return {
                        accuLynxStage: String(accuLynxStage ?? ''),
                        paivStage: String(obj?.paivStage ?? ''),
                        paivStageId: String(obj?.paivStageId ?? ''),
                      };
                    }
                  );
                }
              } catch {}
              return [];
            };

            const stageMappingsArray = (() => {
              const fromTopLevel = parseStageMappings(stageMappingObj);
              if (fromTopLevel.length > 0) return fromTopLevel;
              const fromConfigOptions = parseStageMappings(
                (configOptionsObj as any)?.stageMapping ||
                  (configOptionsObj as any)?.stageMappings
              );
              return fromConfigOptions;
            })();

            const circleId: string =
              (item as any)?.circleId ||
              (configOptionsObj as any)?.circleId ||
              '';
            const circleNameSource: string =
              (configOptionsObj as any)?.circleName ||
              (configOptionsObj as any)?.location ||
              '';

            let circleName: string = '';
            if (circleId) {
              const matched = availableCircles.find((c) => c.id === circleId);
              circleName = matched?.name || '';
            }
            if (!circleName) {
              circleName = circleNameSource;
            }

            const stageCategoryID: string =
              item?.categoryID ||
              (configOptionsObj as any)?.stageCategoryID ||
              '';
            const circleLevelID: string =
              (configOptionsObj as any)?.circleLevelID || '';

            const loc: LocationConfig = {
              id: item?.id,
              apiKey: item.apiKey,
              circleId,
              circleName,
              stageCategoryID,
              circleLevelID,
              accessToken: item.apiKey,
              refreshToken: '',
              tokenExpiration: '',
              connectionStatus: 'untested' as const,
              stageMappings: stageMappingsArray,
            };

            return loc;
          }
        );

        setConfig({
          locations: loadedLocations,
          stageMappings: [],
        });
        setSavedConfig({
          locations: loadedLocations,
          stageMappings: [],
        });
      } else {
        // no existing integrations
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [paivStages]);

  // Load additional data (circles and stages)
  const loadAdditionalData = useCallback(async () => {
    try {
      setIsLoadingCircles(true);
      setIsLoadingStages(true);
      const orgID = (window as any).me?.orgID;
      if (!orgID) return;

      // Load circles
      const circlesResult = (await API.graphql(
        graphqlOperation(listCirclesByOrg, {
          orgID,
          filter: {
            isDeleted: { eq: false },
            isActive: { eq: true },
          },
          limit: 1000,
        })
      )) as GraphQLResult<{ listCirclesByOrg: { items: Circle[] } }>;

      const availableCircles = circlesResult.data?.listCirclesByOrg.items || [];
      setCircles(availableCircles);
      setIsLoadingCircles(false);

      // Load circle levels (non-deleted)
      try {
        const levels = (await fetchCircleLevels()) || [];
        const mapped = levels
          .filter((lvl: any) => lvl && lvl.id && lvl.name)
          .map((lvl: any) => ({ id: lvl.id, name: lvl.name }));
        setCircleLevels(mapped);
      } catch (e) {
        console.warn('Failed to load circle levels', e);
        setCircleLevels([]);
      }

      // Load CRM categories (prefer CrmService; fallback to direct query)
      try {
        const crm = new (CrmService as any)();
        const cats = await crm.getCategoriesWithDependentKPIs(orgID);
        let filtered = Array.isArray(cats) ? cats.slice() : [];
        // Remove LMS only
        filtered = filtered.filter(
          (c: any) =>
            String(c?.title || '')
              .trim()
              .toLowerCase() !== 'lms'
        );
        // Ensure Contacts on top if present
        const contacts = filtered.find(
          (c: any) =>
            String(c?.title || '')
              .trim()
              .toLowerCase() === 'contacts'
        );
        if (contacts) {
          filtered = [
            contacts,
            ...filtered.filter((c: any) => c.id !== contacts.id),
          ];
        }
        const mapped = (filtered || []).map((c: any) => ({
          id: c.id,
          title: c.title,
        }));
        if (mapped.length > 0) {
          setStageCategories(mapped);
          setSingleCrmCategoryId(mapped.length === 1 ? mapped[0].id : null);
        } else {
          throw new Error('Empty categories from CrmService');
        }
      } catch (e) {
        const catsRes = (await API.graphql(
          graphqlOperation(listStageCategorys, {
            filter: { orgID: { eq: orgID }, isDeleted: { eq: false } },
            limit: 1000,
          })
        )) as any;
        const catItems = catsRes.data?.listStageCategorys?.items || [];
        let filteredCats = catItems
          .filter(Boolean)
          .filter((c: any) => c?.id && c?.title)
          .filter(
            (c: any) =>
              String(c?.title || '')
                .trim()
                .toLowerCase() !== 'lms'
          );
        const contacts = catItems.find(
          (c: any) =>
            String(c?.title || '')
              .trim()
              .toLowerCase() === 'contacts'
        );
        if (contacts && !filteredCats.some((c: any) => c.id === contacts.id)) {
          filteredCats = [contacts, ...filteredCats];
        }
        filteredCats.sort((a: any, b: any) => {
          const ak = String(a.title || '')
            .trim()
            .toLowerCase();
          const bk = String(b.title || '')
            .trim()
            .toLowerCase();

          if (ak === 'contacts' && bk !== 'contacts') return -1;
          if (bk === 'contacts' && ak !== 'contacts') return 1;
          return ak.localeCompare(bk);
        });
        setStageCategories(filteredCats);
        setSingleCrmCategoryId(
          filteredCats.length === 1 ? filteredCats[0].id : null
        );
      }

      // Load stages with pagination to get ALL stages
      let allStages: Stage[] = [];
      let nextToken: string | null = null;

      do {
        const stagesResult = (await API.graphql(
          graphqlOperation(listStages, {
            filter: {
              orgID: { eq: orgID },
              isDeleted: { eq: false },
              isActive: { eq: true },
            },
            limit: 1000,
            nextToken,
          })
        )) as GraphQLResult<{
          listStages: { items: Stage[]; nextToken: string | null };
        }>;

        const stageItems = (stagesResult.data?.listStages.items || []).filter(
          (s) => s?.isActive === true
        );

        allStages = [...allStages, ...stageItems];
        nextToken = stagesResult.data?.listStages.nextToken || null;
      } while (nextToken);

      const stages = sortStages(allStages);
      setPaivStages(stages);
      setIsLoadingStages(false);
    } catch (error) {
      setIsLoadingCircles(false);
      setIsLoadingStages(false);
    }
  }, []);

  // Auto-populate a single blank location when nothing saved yet (only after initial load completes)
  useEffect(() => {
    if (
      !isLoading &&
      config.locations.length === 0 &&
      savedConfig.locations.length === 0
    ) {
      setConfig((prev) => ({
        ...prev,
        locations: [
          {
            apiKey: '',
            circleId: '',
            accessToken: '',
            refreshToken: '',
            tokenExpiration: '',
            connectionStatus: 'untested',
            stageCategoryID:
              stageCategories.length > 0 ? stageCategories[0]?.id : undefined,
            stageMappings: [],
          },
        ],
      }));
    }
  }, [
    isLoading,
    config.locations.length,
    savedConfig.locations.length,
    setConfig,
    stageCategories,
  ]);

  // When only one CRM category exists, auto-assign it to any location missing a selection
  useEffect(() => {
    if (singleCrmCategoryId) {
      setConfig((prev) => ({
        ...prev,
        locations: prev.locations.map((loc) => ({
          ...loc,
          stageCategoryID: loc.stageCategoryID || singleCrmCategoryId,
        })),
      }));
    }
  }, [singleCrmCategoryId, setConfig]);

  // Load data on mount
  useEffect(() => {
    loadExistingData();
  }, [loadExistingData]);

  useEffect(() => {
    loadAdditionalData();
  }, [loadAdditionalData]);

  return {
    // State
    isLoading,
    isLoadingCircles,
    isLoadingStages,
    circles,
    paivStages,
    config,
    isEditing,
    savedConfig,

    // Setters
    setConfig,
    setIsEditing,
    setSavedConfig,

    // Loading states
    setIsLoading,
    setIsLoadingCircles,
    setIsLoadingStages,

    // Categories/Levels
    stageCategories,
    circleLevels,
    // UI hints
    hideCrmCategory: Boolean(singleCrmCategoryId),
  };
};
